import { ApiError } from "@/lib/vibrato-api"
import { Modal, ModalBody, ModalFooter, ModalHeader } from "flowbite-react"
import { useContext, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { CampaignContext, VibratoClientContext } from "./contexts"
import { TIMEZONES } from "@/lib/locales"
import { Clock, CheckCircle, WarningTriangle, Play, DashboardDots } from "iconoir-react"

export default function EditCampaign() {
  const { uuid } = useParams()
  const navigate = useNavigate()
  const vibratoClient = useContext(VibratoClientContext)
  const [campaign, setCampaign] = useState<any>(null)
  const parentCampaign = useContext(CampaignContext)
  const [name, setName] = useState("")
  const [validationError, setValidationError] = useState<string>("")
  const [taskPropertyToContactField, setTaskPropertyToContactField] = useState({})
  const [taskTemplateUuid, setTaskTemplateUuid] = useState("")
  const [taskTemplates, setTaskTemplates] = useState([])
  const [message, setMessage] = useState({ text: "", type: "" })
  const [taskTemplatePropertyNames, setTaskTemplatePropertyNames] = useState<string[]>([])
  const [taskTemplatePropertyDescriptions, setTaskTemplatePropertyDescriptions] = useState<string[]>([]) // New state for property descriptions
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [paused, setPaused] = useState(false)
  const [timezone, setTimezone] = useState("America/Los_Angeles")
  const [contactCustomFieldKeys, setContactCustomFieldKeys] = useState<string[]>([])
  const [dailyAvailability, setDailyAvailability] = useState([
    { day: "monday", start: "08:00", end: "17:00" },
    { day: "tuesday", start: "08:00", end: "17:00" },
    { day: "wednesday", start: "08:00", end: "17:00" },
    { day: "thursday", start: "08:00", end: "17:00" },
    { day: "friday", start: "08:00", end: "17:00" },
    { day: "saturday", start: undefined, end: undefined },
    { day: "sunday", start: undefined, end: undefined }
  ])
  const [queuedCalls, setQueuedCalls] = useState<number>(0)
  const [completedCalls, setCompletedCalls] = useState<number>(0)
  const [invalidCalls, setInvalidCalls] = useState<number>(0)
  const [inProgressCalls, setInProgressCalls] = useState<number>(0)
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: any }>({})

  useEffect(() => {
    document.title = "Vibrato - Campaign"
  }, [])

  useEffect(() => {
    vibratoClient.getContactCustomFieldsMetadata().then((meta: any) => {
      setContactCustomFieldKeys(meta.custom_field_keys)
    })
  }, [vibratoClient])

  useEffect(() => {
    if (uuid) {
      const fetchCampaign = async () => {
        try {
          const campaignData = await vibratoClient.getCampaign(uuid)
          setCampaign(campaignData)
          setName(campaignData.name)
          setValidationError(campaignData.validation_error)
          setTaskPropertyToContactField(campaignData.task_property_to_contact_field)
          setTaskTemplateUuid(campaignData.task_template_uuid)
          setTaskTemplatePropertyNames(campaignData.task_template.taskProperties.map((property: any) => property.name))
          setTaskTemplatePropertyDescriptions(
            campaignData.task_template.taskProperties.map((property: any) => property.description)
          ) // Set descriptions
          setTimezone(campaignData.timezone)
          setDailyAvailability(campaignData.daily_availability)
          setPaused(campaignData.paused)
          setQueuedCalls(campaignData.queued_campaign_calls || 0)
          setCompletedCalls(campaignData.completed_campaign_calls || 0)
          setInvalidCalls(campaignData.invalid_campaign_calls || 0)
          setInProgressCalls(campaignData.in_progress_campaign_calls || 0)
        } catch (error) {
          console.error("Failed to fetch campaign:", error)
        }
      }

      fetchCampaign()
    }

    const fetchTaskTemplates = async () => {
      try {
        const templates = await vibratoClient.getTaskTemplates()
        setTaskTemplates(templates)
      } catch (error) {
        console.error("Failed to fetch task templates:", error)
      }
    }

    fetchTaskTemplates()
  }, [uuid, vibratoClient])

  useEffect(() => {
    if (!taskTemplateUuid) return

    if (taskTemplateUuid != campaign?.task_template_uuid) {
      const fetchTaskTemplateProperties = async () => {
        try {
          const taskTemplate = await vibratoClient.getTaskTemplate(taskTemplateUuid)
          setTaskTemplatePropertyNames(taskTemplate.taskProperties.map((property: any) => property.name))
          setTaskTemplatePropertyDescriptions(taskTemplate.taskProperties.map((property: any) => property.description)) // Set descriptions
          setTaskPropertyToContactField({})
        } catch (error) {
          console.error("Failed to fetch task template properties:", error)
        }
      }

      fetchTaskTemplateProperties()

      // Reset field errors when task template changes
      setFieldErrors((prevErrors) => {
        const newErrors = { ...prevErrors }
        delete newErrors.task_property_to_contact_field
        delete newErrors.task_template_uuid
        return newErrors
      })
    }
  }, [taskTemplateUuid, vibratoClient, campaign])

  const handleSubmit = async (event: any) => {
    event.preventDefault()

    // filter taskPropertyToContactField to only include properties that are in the task template
    const filteredTaskPropertyToContactField = Object.keys(taskPropertyToContactField)
      .filter((key) => taskTemplatePropertyNames.includes(key))
      .reduce((obj: any, key: string) => {
        obj[key] = taskPropertyToContactField[key as keyof typeof taskPropertyToContactField]
        return obj
      }, {})

    const campaignData = {
      name,
      task_property_to_contact_field: filteredTaskPropertyToContactField,
      task_template_uuid: taskTemplateUuid,
      timezone,
      paused,
      daily_availability: dailyAvailability
    }

    try {
      const newCampaign = !campaign
      let savedCampaign

      if (campaign) {
        savedCampaign = await vibratoClient.updateCampaign(uuid, campaignData)
      } else {
        savedCampaign = await vibratoClient.createCampaign(campaignData)
      }
      setCampaign(savedCampaign)
      parentCampaign.setCampaign(savedCampaign)

      if (savedCampaign.validation_error) {
        setValidationError(savedCampaign.validation_error)
        setMessage({
          text: "Campaign saved successfully, but there was an issue. See the above warnings for more information.",
          type: "warning"
        })
        setFieldErrors({}) // Clear field errors on successful save
        return
      } else {
        setValidationError("")
      }

      setMessage({ text: "Campaign saved successfully!", type: "success" })
      setFieldErrors({}) // Clear field errors on successful save

      if (newCampaign) {
        navigate(`/campaigns/${savedCampaign.uuid}`)
      }
    } catch (error: any) {
      if (error instanceof ApiError) {
        if (error.data) {
          if (Array.isArray(error.data)) {
            setMessage({
              text: `Error saving campaign: ${error.data.join(", ")}`,
              type: "error"
            })

            return
          }

          const fieldErrors: { [key: string]: string } = {}
          Object.keys(error.data).forEach((key) => {
            if (key === "task_property_to_contact_field") {
              fieldErrors[key] = error.data[key]
            } else {
              fieldErrors[key] = error.data[key].join(", ")
            }
          })

          setFieldErrors(fieldErrors) // Set field errors

          setMessage({
            text: "Error saving campaign. Please correct the errors and try again.",
            type: "error"
          })

          console.error("Failed to save campaign:", error)
        } else {
          setMessage({
            text: "Error saving campaign.",
            type: "error"
          })
        }
      }
    }
  }

  const handlePropertyChange = (propertyName: any, value: any) => {
    setTaskPropertyToContactField((prevMap) => ({
      ...prevMap,
      [propertyName]: value
    }))

    setFieldErrors((prevErrors) => {
      const newErrors = { ...prevErrors }
      delete newErrors.task_property_to_contact_field
      return newErrors
    })
  }

  const handleScheduleChange = (index: any, field: any, value: any) => {
    setDailyAvailability((prevAvailability) =>
      prevAvailability.map((schedule, i) => {
        if (i === index) {
          return { ...schedule, [field]: value }
        }
        return schedule
      })
    )

    setFieldErrors((prevErrors) => {
      const newErrors = { ...prevErrors }
      delete newErrors.daily_availability
      return newErrors
    })
  }

  const deleteCampaign = async () => {
    try {
      await vibratoClient.deleteCampaign(uuid)
    } catch (error) {
      console.error("Failed to delete campaign:", error)
    }
  }

  const pauseCampaign = async () => {
    try {
      await vibratoClient.pauseCampaign(uuid)
      setPaused(true)
      const updatedCampaign = { ...campaign, paused: true }
      setCampaign(updatedCampaign)
      parentCampaign.setCampaign(updatedCampaign)
    } catch (error) {
      console.error("Failed to pause campaign:", error)
    }
  }

  const unpauseCampaign = async () => {
    try {
      await vibratoClient.unpauseCampaign(uuid)
      setPaused(false)
      const updatedCampaign = { ...campaign, paused: false }
      setCampaign(updatedCampaign)
      parentCampaign.setCampaign(updatedCampaign)
    } catch (error) {
      console.error("Failed to unpause campaign:", error)
    }
  }

  const showProps = false

  return (
    <div className="py-8 pr-8">
      <DeleteConfirmationModal
        show={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={async () => {
          await deleteCampaign()
        }}
      />

      {/* Campaign Analytics Dashboard */}
      {uuid && (
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="p-2 sm:p-3 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-lg sm:rounded-xl shadow-sm">
              <DashboardDots width="20" height="20" className="sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Campaign Analytics</h3>
              <p className="text-sm sm:text-base text-gray-600">Monitor your campaign performance in real-time</p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6 shadow-md hover:shadow-lg transition-all duration-200 border border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 lg:gap-4 h-full">
                <div className="p-2 sm:p-2.5 lg:p-3 bg-amber-100 rounded-lg sm:rounded-xl shrink-0 self-start">
                  <Play className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-amber-600" />
                </div>
                <div className="flex flex-col h-full min-w-0">
                  <h4 className="text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2 truncate">In Progress</h4>
                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2">
                    <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">{inProgressCalls}</span>
                    <span className="text-xs sm:text-sm text-amber-600 font-medium">Active</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6 shadow-md hover:shadow-lg transition-all duration-200 border border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 lg:gap-4 h-full">
                <div className="p-2 sm:p-2.5 lg:p-3 bg-blue-100 rounded-lg sm:rounded-xl shrink-0 self-start">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-blue-600" />
                </div>
                <div className="flex flex-col h-full min-w-0">
                  <h4 className="text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2 truncate">Scheduled</h4>
                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2">
                    <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">{queuedCalls}</span>
                    <span className="text-xs sm:text-sm text-blue-600 font-medium">Queued</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6 shadow-md hover:shadow-lg transition-all duration-200 border border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 lg:gap-4 h-full">
                <div className="p-2 sm:p-2.5 lg:p-3 bg-green-100 rounded-lg sm:rounded-xl shrink-0 self-start">
                  <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-green-600" />
                </div>
                <div className="flex flex-col h-full min-w-0">
                  <h4 className="text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2 truncate">Completed</h4>
                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2">
                    <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">{completedCalls}</span>
                    <span className="text-xs sm:text-sm text-green-600 font-medium">Success</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6 shadow-md hover:shadow-lg transition-all duration-200 border border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 lg:gap-4 h-full">
                <div className="p-2 sm:p-2.5 lg:p-3 bg-red-100 rounded-lg sm:rounded-xl shrink-0 self-start">
                  <WarningTriangle className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-red-600" />
                </div>
                <div className="flex flex-col h-full min-w-0">
                  <h4 className="text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2 truncate">
                    Invalid Contacts
                  </h4>
                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2">
                    <span className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">{invalidCalls}</span>
                    <span className="text-xs sm:text-sm text-red-600 font-medium">Errors</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {uuid && <h2 className="text-xl font-bold mb-4">Settings</h2>}
        {!uuid && <h1 className="text-xl font-bold mb-6">Create Campaign</h1>}

        <div className="font-thin">
          In this section, you can define the execution parameters for the task template, which will be run once for
          every contact within the campaign.
        </div>

        {/* Form fields for campaign details */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Campaign Name
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-base"
            required
          />
          {fieldErrors.name && <p className="text-red-500 text-xs italic mt-2">{fieldErrors.name}</p>}
        </div>

        {paused ? (
          <button
            type="button"
            onClick={unpauseCampaign}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Unpause Campaign
          </button>
        ) : (
          <button
            type="button"
            onClick={pauseCampaign}
            className="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded"
          >
            Pause Campaign
          </button>
        )}

        <hr className="h-px my-8 bg-gray-200 border-0 dark:bg-gray-700" />
        <h2 className="text-xl font-bold mb-6">Task Template</h2>

        {validationError && (
          <div
            className="p-4 mb-4 text-sm text-yellow-800 rounded-lg bg-yellow-50 dark:bg-gray-800 dark:text-yellow-300"
            role="alert"
          >
            {validationError.split("\n").map((error, index) => (
              <div key={`validation-${index.toString()}`}>
                {index === 0 ? <span className="font-medium">Warning: </span> : null} {error}
              </div>
            ))}
          </div>
        )}

        <div className="font-thin">
          Choose a task template to run for each contact in the campaign. You can also map each of the task template
          parameters to a contact field. When the task is run for a contact, the contact field value will be passed as
          the parameter to the task.
        </div>

        {/* Task Template Selection */}
        <div>
          <label htmlFor="taskTemplateUuid" className="block text-sm font-medium text-gray-700">
            Task Template
          </label>

          <select
            id="taskTemplateUuid"
            value={taskTemplateUuid}
            onChange={(e) => setTaskTemplateUuid(e.target.value)}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-base"
            required
          >
            <option value="">Select a Task Template</option>
            {taskTemplates.map((template: any) => (
              <option key={template.uuid} value={template.uuid}>
                {template.taskName}
              </option>
            ))}
          </select>
          {fieldErrors.task_template_uuid && (
            <p className="text-red-500 text-xs italic mt-2">{fieldErrors.task_template_uuid}</p>
          )}
        </div>

        {showProps && taskTemplatePropertyNames.length > 0 && (
          <>
            <div className="flex flex-col gap-2">
              <div className="mb-2">
                <label htmlFor="taskTemplateUuid" className="block text-sm font-medium text-gray-700">
                  Parameters
                </label>
                <p className="text-sm text-gray-500 mt-2">
                  Map task template parameter names to contact custom fields. When the task is run for a contact, the
                  contact field value will be passed as the parameter to the task template.
                </p>
              </div>

              {fieldErrors.task_property_to_contact_field?.length > 0 && (
                <p className="text-red-500 text-xs italic">{fieldErrors.task_property_to_contact_field.join(", ")}</p>
              )}

              <div className="grid grid-cols-2 gap-4">
                {taskTemplatePropertyNames.map((propertyName: string, index: number) => (
                  <div
                    key={`template-prop-${index.toString()}`}
                    className="p-4 border border-gray-300 rounded-lg bg-gray-50 mb-4"
                  >
                    <label htmlFor={propertyName} className="block text-sm font-medium text-gray-700">
                      {propertyName}
                    </label>
                    <p className="text-xs text-gray-500 mt-1">{taskTemplatePropertyDescriptions[index]}</p>
                    <select
                      id={propertyName}
                      value={taskPropertyToContactField[propertyName as keyof typeof taskPropertyToContactField] || ""}
                      onChange={(e) => handlePropertyChange(propertyName, e.target.value)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-base"
                      required
                    >
                      <option value="">Select a Contact Field</option>
                      {contactCustomFieldKeys.map((key) => (
                        <option key={key} value={key}>
                          {key}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.task_property_to_contact_field?.[propertyName] && (
                      <p className="text-red-500 text-xs italic mt-2">
                        {fieldErrors.task_property_to_contact_field[propertyName].join(", ")}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <hr className="h-px my-8 bg-gray-200 border-0 dark:bg-gray-700" />
        <h2 className="text-xl font-bold mb-6">Weekly Calling Hours</h2>

        <div className="font-thin">
          These are the hours when our system will try to call each contact enrolled in the campaign.
        </div>

        <div>
          <label htmlFor="timezone" className="block text-sm font-medium text-gray-700">
            Timezone
          </label>
          <select
            id="timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-base"
            required
          >
            <option value="">Select a Timezone</option>
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
          {fieldErrors.timezone && <p className="text-red-500 text-xs italic mt-2">{fieldErrors.timezone}</p>}
        </div>

        {/* Daily Availability Schedule */}
        <div className="flex flex-col">
          {dailyAvailability.map((schedule, index) => (
            <div key={schedule.day} className="flex place-items-center gap-2">
              <input
                type="checkbox"
                id={`availability-checkbox-${schedule.day}`}
                checked={schedule.start != null && schedule.end != null}
                onChange={(e) => {
                  // If the checkbox is checked, set default times, otherwise clear them
                  const start = e.target.checked ? "08:00" : undefined
                  const end = e.target.checked ? "17:00" : undefined
                  handleScheduleChange(index, "start", start)
                  handleScheduleChange(index, "end", end)
                }}
                className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <label className="block text-sm font-medium text-gray-700 min-w-[80px] text-left ml-2">
                {schedule.day.charAt(0).toUpperCase() + schedule.day.slice(1)}
              </label>
              <div className="flex gap-2">
                <input
                  type="time"
                  value={schedule.start || ""}
                  onChange={(e) => handleScheduleChange(index, "start", e.target.value)}
                  className="mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-base min-w-[120px]"
                />
                <input
                  type="time"
                  value={schedule.end || ""}
                  onChange={(e) => handleScheduleChange(index, "end", e.target.value)}
                  className="mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-base min-w-[120px]"
                />
              </div>
            </div>
          ))}
        </div>
        {fieldErrors.daily_availability && (
          <p className="text-red-500 text-xs italic mt-2">{fieldErrors.daily_availability}</p>
        )}

        <hr className="h-px my-8 bg-gray-200 border-0 dark:bg-gray-700" />
        <div className="flex gap-2">
          <button type="submit" className="bg-green-400 hover:bg-green-700 text-white font-bold py-2 px-4 rounded">
            Save
          </button>
          {uuid && (
            <div className="flex grow">
              <button
                type="button"
                onClick={() => setShowDeleteConfirmation(true)}
                className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
              >
                Delete
              </button>
            </div>
          )}
        </div>
        {message.text && (
          <div
            className={`mt-3 text-sm font-medium rounded-md ${
              message.type === "success"
                ? "text-green-500"
                : message.type === "warning"
                  ? "text-yellow-500"
                  : "text-red-700"
            }`}
          >
            {message.text}
          </div>
        )}
      </form>
    </div>
  )
}

function DeleteConfirmationModal({
  show,
  onClose,
  onConfirm
}: {
  show: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  const navigate = useNavigate()
  const [message, setMessage] = useState("")

  const onDelete = async () => {
    try {
      await onConfirm()
      setMessage("Campaign deleted.")
      await new Promise((resolve) => setTimeout(resolve, 1000))
      navigate("/campaigns")
    } catch (error) {
      console.error("Failed to delete campaign:", error)
      setMessage("Error deleting campaign.")
    }
  }

  return (
    <Modal show={show} onClose={onClose} className="pt-safe">
      <ModalHeader className="border-b border-gray-200">
        <span className="text-purple-600">Delete Campaign</span>
      </ModalHeader>
      <ModalBody>
        <p>Are you sure you want to delete this campaign?</p>
      </ModalBody>
      <ModalFooter>
        {!message && (
          <>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-fit"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="mt-2 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded w-fit"
            >
              Delete
            </button>
          </>
        )}
        {message && <div className="text-green-500">{message}</div>}
      </ModalFooter>
    </Modal>
  )
}
